import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  ArrowLeft,
  CaretLeft,
  CaretRight,
  Phone,
  Pencil,
  Calendar as CalendarIcon,
  Bell,
  CurrencyEur,
  CaretRight as CaretRightIcon,
  CheckCircle,
  Note as NoteIcon,
  CaretUp,
  CaretDown,
  CaretUpDown,
  ArrowsClockwise,
  Trash,
  MagnifyingGlass,
} from "@phosphor-icons/react";
import ContractDialog from "./ContractDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GR_MONTHS = [
  "ΙΑΝΟΥΑΡΙΟΣ",
  "ΦΕΒΡΟΥΑΡΙΟΣ",
  "ΜΑΡΤΙΟΣ",
  "ΑΠΡΙΛΙΟΣ",
  "ΜΑΪΟΣ",
  "ΙΟΥΝΙΟΣ",
  "ΙΟΥΛΙΟΣ",
  "ΑΥΓΟΥΣΤΟΣ",
  "ΣΕΠΤΕΜΒΡΙΟΣ",
  "ΟΚΤΩΒΡΙΟΣ",
  "ΝΟΕΜΒΡΙΟΣ",
  "ΔΕΚΕΜΒΡΙΟΣ",
];
const GR_MONTHS_SHORT = [
  "ΙΑΝ",
  "ΦΕΒ",
  "ΜΑΡ",
  "ΑΠΡ",
  "ΜΑΙ",
  "ΙΟΥΝ",
  "ΙΟΥΛ",
  "ΑΥΓ",
  "ΣΕΠ",
  "ΟΚΤ",
  "ΝΟΕ",
  "ΔΕΚ",
];
const GR_DOW = ["ΔΕ", "ΤΡ", "ΤΕ", "ΠΕ", "ΠΑ", "ΣΑ", "ΚΥ"]; // Mon-Sun
const GR_DOW_LONG = [
  "ΔΕΥΤΕΡΑ",
  "ΤΡΙΤΗ",
  "ΤΕΤΑΡΤΗ",
  "ΠΕΜΠΤΗ",
  "ΠΑΡΑΣΚΕΥΗ",
  "ΣΑΒΒΑΤΟ",
  "ΚΥΡΙΑΚΗ",
];

function ymd(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function parseISO(s) {
  if (!s) return null;
  const [y, m, d] = s.split("T")[0].split("-").map((n) => parseInt(n, 10));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function fmtGR(s) {
  const d = parseISO(s);
  if (!d) return "—";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

function daysUntil(s) {
  const d = parseISO(s);
  if (!d) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / 86400000);
  return diff;
}

function itemsLabel(c) {
  if (Array.isArray(c.items) && c.items.length) {
    return c.items
      .map((it) => (it.qty > 1 ? `${it.kind} x${it.qty}` : it.kind))
      .join(" + ");
  }
  return c.kind || "";
}

function fmtMoney(v) {
  const n = parseFloat(v);
  if (!n) return "";
  return n.toLocaleString("el-GR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// Start-of-week (Monday) for a given Date
function startOfWeek(d) {
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = (out.getDay() + 6) % 7; // Mon=0
  out.setDate(out.getDate() - dow);
  return out;
}

export default function ContractsView({ onExit, onRenewed, initialContractId }) {
  const [contracts, setContracts] = useState([]);
  const [kinds, setKinds] = useState({ defaults: [], custom: [] });
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  // view: 'year' | 'month' | 'week'
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  });
  const [selectedDay, setSelectedDay] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [list, kindsResp] = await Promise.all([
        axios.get(`${API}/contracts`),
        axios.get(`${API}/contracts/kinds`),
      ]);
      setContracts(list.data);
      setKinds(kindsResp.data);
    } catch (e) {
      toast.error("ΣΦΑΛΜΑ");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Auto-refresh every 30s so new/edited contracts from other PCs appear.
  // Pause when the dialog is open (user is editing) or tab hidden.
  useEffect(() => {
    const tick = () => {
      if (dialogOpen) return;
      if (typeof document !== "undefined" && document.hidden) return;
      // silent: do not toggle the loading spinner
      axios
        .get(`${API}/contracts`)
        .then(({ data }) => setContracts(data))
        .catch(() => {});
    };
    const h = setInterval(tick, 30000);
    return () => clearInterval(h);
  }, [dialogOpen]);

  // When asked to open a specific contract (deep-link from a post-it), find it
  // and open the dialog as soon as contracts are loaded. Also jump the calendar
  // cursor to its expiry month. We track the last consumed id so that re-loads
  // (e.g. after Save) don't reopen the dialog.
  const consumedInitialIdRef = useRef(null);
  useEffect(() => {
    if (!initialContractId || !contracts.length) return;
    if (consumedInitialIdRef.current === initialContractId) return;
    const c = contracts.find((x) => x.id === initialContractId);
    if (!c) {
      consumedInitialIdRef.current = initialContractId;
      toast.error("ΔΕΝ ΒΡΕΘΗΚΕ ΤΟ ΣΥΜΒΟΛΑΙΟ");
      return;
    }
    consumedInitialIdRef.current = initialContractId;
    const exp = parseISO(c.expiry_date);
    if (exp) setCursor(new Date(exp.getFullYear(), exp.getMonth(), 1));
    setEditing(c);
    setDialogOpen(true);
  }, [initialContractId, contracts]);

  // Group expirations and notifications by YYYY-MM-DD
  const eventsByDay = useMemo(() => {
    const map = {};
    for (const c of contracts) {
      if (c.expiry_date) {
        const k = c.expiry_date;
        if (!map[k]) map[k] = { expiries: [], notifications: [] };
        map[k].expiries.push(c);
      }
      if (c.notification_date) {
        const k = c.notification_date;
        if (!map[k]) map[k] = { expiries: [], notifications: [] };
        map[k].notifications.push(c);
      }
    }
    return map;
  }, [contracts]);

  const monthExpiries = useMemo(() => {
    const y = cursor.getFullYear();
    const m = cursor.getMonth();
    return contracts
      .filter((c) => {
        const d = parseISO(c.expiry_date);
        return d && d.getFullYear() === y && d.getMonth() === m;
      })
      .sort((a, b) => a.expiry_date.localeCompare(b.expiry_date));
  }, [contracts, cursor]);

  const yearExpiries = useMemo(() => {
    const y = cursor.getFullYear();
    return contracts.filter((c) => {
      const d = parseISO(c.expiry_date);
      return d && d.getFullYear() === y;
    });
  }, [contracts, cursor]);

  const weekStart = useMemo(() => startOfWeek(cursor), [cursor]);
  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      return d;
    });
  }, [weekStart]);

  const todayKey = ymd(new Date());

  const openNew = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setDialogOpen(true);
  };
  const handleDelete = async (c) => {
    if (!window.confirm(`ΔΙΑΓΡΑΦΗ ΣΥΜΒΟΛΑΙΟΥ "${c.customer}";`)) return;
    try {
      await axios.delete(`${API}/contracts/${c.id}`);
      toast.success("ΔΙΑΓΡΑΦΗΚΕ");
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error("ΣΦΑΛΜΑ");
    }
  };
  const handleRenew = async (c) => {
    if (
      !window.confirm(
        `ΔΗΜΙΟΥΡΓΙΑ POST-IT ΠΑΡΑΓΓΕΛΙΑΣ ΓΙΑ ΑΝΑΝΕΩΣΗ "${c.customer}";`
      )
    )
      return;
    try {
      const resp = await axios.post(`${API}/contracts/${c.id}/renew`);
      const seq = resp?.data?.note_seq || "";
      toast.success(
        seq
          ? `ΔΗΜΙΟΥΡΓΗΘΗΚΕ POST #${seq} ΣΤΟ PINBOARD`
          : "ΔΗΜΙΟΥΡΓΗΘΗΚΕ"
      );
      setDialogOpen(false);
      // Notify parent so the board refreshes its notes list
      if (onRenewed) onRenewed();
      // Take the user back to the PinBoard so they immediately see the new post-it
      if (onExit) onExit();
    } catch (e) {
      const msg = e?.response?.data?.detail || "ΣΦΑΛΜΑ";
      toast.error(typeof msg === "string" ? msg : "ΣΦΑΛΜΑ");
    }
  };
  const handleSave = async (payload, id) => {
    try {
      if (id) {
        await axios.put(`${API}/contracts/${id}`, payload);
        toast.success("ΑΠΟΘΗΚΕΥΤΗΚΕ");
      } else {
        await axios.post(`${API}/contracts`, payload);
        toast.success("ΚΑΤΑΧΩΡΗΘΗΚΕ");
        // Ask if it should also be saved in ΚΩΔΙΚΟΙ
        if (window.confirm("ΘΕΛΕΙΣ ΝΑ ΓΙΝΕΙ ΕΓΓΡΑΦΗ ΚΑΙ ΣΤΟ ΜΕΝΟΥ 'ΚΩΔΙΚΟΙ';")) {
          try {
            await axios.post(`${API}/codes`, {
              customer: payload.customer,
              code: payload.code || "",
              phone: payload.phone || "",
              activation_date: payload.activation_date || "",
              expiry_date: payload.expiry_date || "",
              items: payload.items || [],
              notes: payload.notes || "",
              codes: "",
            });
            toast.success("ΕΓΓΡΑΦΗ ΣΤΟΥΣ ΚΩΔΙΚΟΥΣ");
          } catch {
            toast.error("ΑΠΟΤΥΧΙΑ ΕΓΓΡΑΦΗΣ ΚΩΔΙΚΩΝ");
          }
        }
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Σφάλμα";
      toast.error(typeof msg === "string" ? msg : "Σφάλμα");
    }
  };

  // Navigation depends on view
  const navPrev = () => {
    if (view === "year") {
      setCursor(new Date(cursor.getFullYear() - 1, cursor.getMonth(), 1));
    } else if (view === "month") {
      setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
    } else {
      const d = new Date(cursor);
      d.setDate(d.getDate() - 7);
      setCursor(d);
    }
  };
  const navNext = () => {
    if (view === "year") {
      setCursor(new Date(cursor.getFullYear() + 1, cursor.getMonth(), 1));
    } else if (view === "month") {
      setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
    } else {
      const d = new Date(cursor);
      d.setDate(d.getDate() + 7);
      setCursor(d);
    }
  };
  const navToday = () => setCursor(new Date());

  const headerLabel = useMemo(() => {
    if (view === "year") return String(cursor.getFullYear());
    if (view === "month") return GR_MONTHS[cursor.getMonth()];
    // week label: range Mon-Sun
    const end = new Date(weekStart);
    end.setDate(end.getDate() + 6);
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(weekStart.getDate())}/${pad(weekStart.getMonth() + 1)} - ${pad(end.getDate())}/${pad(end.getMonth() + 1)}`;
  }, [view, cursor, weekStart]);

  const subHeaderLabel = useMemo(() => {
    if (view === "year") return "";
    return String(cursor.getFullYear());
  }, [view, cursor]);

  return (
    <div className="min-h-screen board-bg" data-testid="contracts-view">
      <header className="sticky top-0 z-30 board-bg/95 backdrop-blur-sm border-b border-black/5 px-6 pt-4 pb-3 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <button
            onClick={onExit}
            data-testid="contracts-back-btn"
            className="px-3 py-2 rounded-full flex items-center gap-2 transition-all text-xs font-bold uppercase tracking-wider border-2 bg-white/60 text-ink border-ink/15 hover:border-ink/40 active:scale-95"
            title="ΕΠΙΣΤΡΟΦΗ"
          >
            <ArrowLeft size={14} weight="bold" />
            ΠΙΣΩ
          </button>
          <div>
            <h1
              className="font-caveat text-4xl font-bold text-ink leading-none flex items-center gap-2"
              data-testid="contracts-title"
            >
              <CalendarIcon size={28} weight="bold" />
              ΑΝΑΝΕΩΣΕΙΣ
            </h1>
            <p className="text-[10px] uppercase tracking-[0.2em] text-ink-muted mt-1 font-bold">
              {contracts.length} ΣΥΜΒΟΛΑΙΑ ·{" "}
              {view === "year"
                ? `${yearExpiries.length} ΛΗΞΕΙΣ ${cursor.getFullYear()}`
                : view === "month"
                ? `${monthExpiries.length} ΛΗΞΕΙΣ ${GR_MONTHS[cursor.getMonth()]}`
                : view === "week"
                ? "ΠΡΟΒΟΛΗ ΕΒΔΟΜΑΔΑΣ"
                : "ΟΛΕΣ ΟΙ ΣΥΜΒΑΣΕΙΣ"}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <ViewSwitch view={view} onChange={setView} />
          <button
            onClick={openNew}
            data-testid="contracts-new-btn"
            className="bg-ink text-white pl-4 pr-5 py-2.5 rounded-full flex items-center gap-2 hover:bg-ink/85 active:scale-95 transition-all text-sm font-bold uppercase tracking-wider"
          >
            <Plus size={16} weight="bold" />
            ΝΕΑ ΕΓΓΡΑΦΗ
          </button>
        </div>
      </header>

      <div
        className={`px-6 py-5 ${
          view === "year" || view === "all"
            ? "grid grid-cols-1"
            : "grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-5"
        }`}
      >
        {view === "all" ? (
          <AllContractsTable
            items={contracts}
            onOpen={openEdit}
            onRenew={handleRenew}
            onDelete={handleDelete}
          />
        ) : (
          <>
        {/* Calendar pane */}
        <div
          className="rounded-2xl bg-white/80 border border-black/10 p-4 shadow-sm"
          data-testid="contracts-calendar"
        >
          <div className="flex items-center justify-between mb-3 gap-2">
            <button
              onClick={navPrev}
              data-testid="calendar-prev"
              className="p-2 rounded-lg hover:bg-black/5 transition-colors"
              title="ΠΡΟΗΓΟΥΜΕΝΟ"
            >
              <CaretLeft size={18} weight="bold" />
            </button>
            <div className="text-center flex-1">
              <button
                onClick={navToday}
                data-testid="calendar-today"
                className="font-caveat text-3xl font-bold text-ink leading-none hover:text-ink/70 transition-colors"
                title="ΣΗΜΕΡΑ"
              >
                {headerLabel}
              </button>
              {subHeaderLabel && (
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-ink/55 mt-0.5">
                  {subHeaderLabel}
                </div>
              )}
            </div>
            <button
              onClick={navNext}
              data-testid="calendar-next"
              className="p-2 rounded-lg hover:bg-black/5 transition-colors"
              title="ΕΠΟΜΕΝΟ"
            >
              <CaretRight size={18} weight="bold" />
            </button>
          </div>

          {view === "year" && (
            <YearView
              year={cursor.getFullYear()}
              eventsByDay={eventsByDay}
              todayKey={todayKey}
              onMonthClick={(m) => {
                setCursor(new Date(cursor.getFullYear(), m, 1));
                setView("month");
              }}
              onDayClick={(d) => {
                setSelectedDay(d);
                setCursor(d);
                setView("month");
              }}
            />
          )}

          {view === "month" && (
            <MonthView
              cursor={cursor}
              eventsByDay={eventsByDay}
              todayKey={todayKey}
              selectedDay={selectedDay}
              onSelect={(d) => setSelectedDay(d)}
              onOpen={openEdit}
            />
          )}

          {view === "week" && (
            <WeekView
              weekDays={weekDays}
              eventsByDay={eventsByDay}
              todayKey={todayKey}
              onOpen={openEdit}
            />
          )}

          {/* Day details (month view only) */}
          {view === "month" && selectedDay && (
            <div
              className="mt-4 pt-3 border-t border-black/5"
              data-testid="day-detail"
            >
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-ink/55 mb-2">
                ΛΗΞΕΙΣ {fmtGR(ymd(selectedDay))}
              </div>
              <div className="space-y-1.5">
                {(eventsByDay[ymd(selectedDay)]?.expiries || []).map((c) => (
                  <ContractRow
                    key={c.id}
                    c={c}
                    onOpen={() => openEdit(c)}
                  />
                ))}
                {(eventsByDay[ymd(selectedDay)]?.expiries || []).length === 0 && (
                  <p className="text-xs italic text-ink/40">ΧΩΡΙΣ ΛΗΞΕΙΣ</p>
                )}
              </div>
              {(eventsByDay[ymd(selectedDay)]?.notifications || []).length >
                0 && (
                <>
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-amber-700 mb-2 mt-3 flex items-center gap-1">
                    <Bell size={11} weight="bold" /> ΕΝΗΜΕΡΩΣΕΙΣ{" "}
                    {fmtGR(ymd(selectedDay))}
                  </div>
                  <div className="space-y-1.5">
                    {(eventsByDay[ymd(selectedDay)]?.notifications || []).map(
                      (c) => (
                        <ContractRow
                          key={`n-${c.id}`}
                          c={c}
                          onOpen={() => openEdit(c)}
                          isNotification
                        />
                      )
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Side panel: visible in month/week views */}
        {view !== "year" && view !== "all" && (
          <div className="rounded-2xl bg-white/80 border border-black/10 p-4 shadow-sm">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-ink/70 mb-3">
              ΛΗΞΕΙΣ {GR_MONTHS[cursor.getMonth()]} ({monthExpiries.length})
            </div>
            {loading ? (
              <p className="text-xs italic text-ink/40">ΦΟΡΤΩΣΗ...</p>
            ) : monthExpiries.length === 0 ? (
              <p className="text-xs italic text-ink/40">
                ΧΩΡΙΣ ΛΗΞΕΙΣ ΑΥΤΟΝ ΤΟΝ ΜΗΝΑ
              </p>
            ) : (
              <div
                className="space-y-2 max-h-[68vh] overflow-y-auto"
                data-testid="month-expiries-list"
              >
                {monthExpiries.map((c) => (
                  <ContractRow
                    key={c.id}
                    c={c}
                    onOpen={() => openEdit(c)}
                    showDate
                  />
                ))}
              </div>
            )}
          </div>
        )}
          </>
        )}
      </div>

      <ContractDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={editing}
        kinds={kinds}
        onSave={handleSave}
        onRenew={handleRenew}
        onDelete={handleDelete}
      />
    </div>
  );
}

/* ---------------- View Switch ---------------- */
function ViewSwitch({ view, onChange }) {
  const opts = [
    { v: "year", l: "ΕΤΟΣ" },
    { v: "month", l: "ΜΗΝΑΣ" },
    { v: "week", l: "ΕΒΔΟΜΑΔΑ" },
    { v: "all", l: "ΟΛΕΣ ΟΙ ΣΥΜΒΑΣΕΙΣ" },
  ];
  return (
    <div
      className="inline-flex rounded-full border-2 border-ink/15 bg-white/60 p-0.5"
      data-testid="calendar-view-switch"
    >
      {opts.map((o) => (
        <button
          key={o.v}
          onClick={() => onChange(o.v)}
          data-testid={`calendar-view-${o.v}`}
          className={`px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.15em] transition-all ${
            view === o.v
              ? "bg-ink text-white shadow"
              : "text-ink/65 hover:text-ink"
          }`}
        >
          {o.l}
        </button>
      ))}
    </div>
  );
}

/* ---------------- Month View ---------------- */
function MonthView({ cursor, eventsByDay, todayKey, selectedDay, onSelect, onOpen }) {
  const grid = useMemo(() => {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const first = new Date(year, month, 1);
    const firstDow = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push(new Date(year, month, d));
    }
    while (cells.length < 42) cells.push(null);
    return cells;
  }, [cursor]);

  return (
    <>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {GR_DOW.map((d) => (
          <div
            key={d}
            className="text-center text-[10px] uppercase tracking-[0.2em] font-bold text-ink/45 py-1"
          >
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {grid.map((d, i) => {
          if (!d) {
            return (
              <div
                key={i}
                className="min-h-[88px] rounded-lg bg-black/[0.02]"
              />
            );
          }
          const key = ymd(d);
          const ev = eventsByDay[key] || { expiries: [], notifications: [] };
          const expiries = ev.expiries || [];
          const notifications = ev.notifications || [];
          const isToday = key === todayKey;
          const isSelected = selectedDay && ymd(selectedDay) === key;
          const hasEv = expiries.length > 0 || notifications.length > 0;
          return (
            <button
              key={i}
              onClick={() => onSelect(d)}
              data-testid={`calendar-day-${key}`}
              className={`min-h-[88px] rounded-lg border text-left p-1.5 flex flex-col gap-1 transition-all hover:border-ink/40 ${
                isSelected
                  ? "bg-amber-100 border-amber-400 shadow-inner"
                  : isToday
                  ? "bg-white border-ink/40 ring-1 ring-ink/20"
                  : hasEv
                  ? "bg-white border-black/10"
                  : "bg-white/60 border-black/5"
              }`}
            >
              <div className="flex items-center justify-between">
                <div
                  className={`text-xs font-black tabular-nums ${
                    isToday ? "text-ink" : "text-ink/65"
                  }`}
                >
                  {d.getDate()}
                </div>
                {expiries.length > 0 && (
                  <span className="text-[9px] font-black text-red-700 bg-red-50 border border-red-200 rounded px-1 leading-none py-0.5">
                    {expiries.length}
                  </span>
                )}
              </div>

              {/* Up to 2 expiries shown */}
              <div className="flex flex-col gap-0.5 mt-0.5 min-h-[36px]">
                {expiries.slice(0, 2).map((e) => (
                  <span
                    key={e.id}
                    onClick={(ev2) => {
                      ev2.stopPropagation();
                      onOpen(e);
                    }}
                    role="button"
                    title={`${e.customer} — ${itemsLabel(e)}`}
                    className="block text-[9px] font-bold text-red-800 bg-red-50 border-l-2 border-red-500 px-1 rounded-sm truncate hover:bg-red-100 transition-colors"
                  >
                    {e.customer}
                  </span>
                ))}
                {expiries.length > 2 && (
                  <span className="text-[8px] font-black text-red-700 leading-tight">
                    +{expiries.length - 2} ΑΚΟΜΑ
                  </span>
                )}
                {notifications.length > 0 && (
                  <span
                    title="ΕΝΗΜΕΡΩΣΕΙΣ"
                    className="inline-flex items-center gap-0.5 text-[9px] font-black text-amber-800 bg-amber-50 border-l-2 border-amber-500 px-1 rounded-sm w-fit"
                  >
                    <Bell size={9} weight="bold" />
                    {notifications.length}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </>
  );
}

/* ---------------- Year View ---------------- */
function YearView({ year, eventsByDay, todayKey, onMonthClick, onDayClick }) {
  return (
    <div
      className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4"
      data-testid="year-grid"
    >
      {Array.from({ length: 12 }, (_, m) => (
        <MiniMonth
          key={m}
          year={year}
          month={m}
          eventsByDay={eventsByDay}
          todayKey={todayKey}
          onMonthClick={() => onMonthClick(m)}
          onDayClick={onDayClick}
        />
      ))}
    </div>
  );
}

function MiniMonth({ year, month, eventsByDay, todayKey, onMonthClick, onDayClick }) {
  const grid = useMemo(() => {
    const first = new Date(year, month, 1);
    const firstDow = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    while (cells.length < 42) cells.push(null);
    return cells;
  }, [year, month]);

  const monthCount = useMemo(() => {
    let n = 0;
    for (const d of grid) {
      if (!d) continue;
      n += (eventsByDay[ymd(d)]?.expiries || []).length;
    }
    return n;
  }, [grid, eventsByDay]);

  return (
    <div
      className="rounded-xl border border-black/10 bg-white p-2 shadow-sm"
      data-testid={`year-month-${month}`}
    >
      <button
        onClick={onMonthClick}
        className="w-full flex items-center justify-between mb-1.5 hover:opacity-80 transition-opacity"
      >
        <span className="font-caveat text-xl font-bold text-ink leading-none">
          {GR_MONTHS_SHORT[month]}
        </span>
        {monthCount > 0 && (
          <span className="text-[9px] font-black text-red-700 bg-red-50 border border-red-200 rounded px-1 py-0.5 leading-none">
            {monthCount}
          </span>
        )}
      </button>
      <div className="grid grid-cols-7 gap-0.5">
        {GR_DOW.map((d) => (
          <div
            key={d}
            className="text-center text-[7px] uppercase tracking-wider font-bold text-ink/40"
          >
            {d.charAt(0)}
          </div>
        ))}
        {grid.map((d, i) => {
          if (!d) return <div key={i} className="aspect-square" />;
          const key = ymd(d);
          const count = (eventsByDay[key]?.expiries || []).length;
          const hasNotif = (eventsByDay[key]?.notifications || []).length > 0;
          const isToday = key === todayKey;
          return (
            <button
              key={i}
              onClick={() => onDayClick(d)}
              data-testid={`year-day-${key}`}
              title={count > 0 ? `${count} ΛΗΞΕΙΣ` : ""}
              className={`aspect-square rounded text-[9px] font-bold tabular-nums flex items-center justify-center relative transition-colors ${
                count > 0
                  ? "bg-red-100 text-red-800 hover:bg-red-200"
                  : isToday
                  ? "bg-ink text-white"
                  : "text-ink/60 hover:bg-black/5"
              }`}
            >
              {d.getDate()}
              {hasNotif && (
                <span className="absolute top-0 right-0 w-1 h-1 rounded-full bg-amber-500" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------- Week View ---------------- */
function WeekView({ weekDays, eventsByDay, todayKey, onOpen }) {
  return (
    <div
      className="grid grid-cols-1 md:grid-cols-7 gap-2"
      data-testid="week-grid"
    >
      {weekDays.map((d, i) => {
        const key = ymd(d);
        const ev = eventsByDay[key] || { expiries: [], notifications: [] };
        const expiries = ev.expiries || [];
        const notifications = ev.notifications || [];
        const isToday = key === todayKey;
        return (
          <div
            key={i}
            data-testid={`week-day-${key}`}
            className={`rounded-xl border p-2 min-h-[260px] flex flex-col ${
              isToday
                ? "bg-amber-50 border-amber-300"
                : "bg-white border-black/10"
            }`}
          >
            <div className="flex items-baseline justify-between mb-2 pb-1.5 border-b border-black/5">
              <div>
                <div className="text-[9px] uppercase tracking-[0.2em] font-bold text-ink/55">
                  {GR_DOW_LONG[i]}
                </div>
                <div className="text-2xl font-black tabular-nums text-ink leading-none mt-0.5">
                  {d.getDate()}
                </div>
              </div>
              <div className="text-[9px] font-bold text-ink/45 uppercase">
                {GR_MONTHS_SHORT[d.getMonth()]}
              </div>
            </div>
            <div className="space-y-1 flex-1 overflow-y-auto">
              {expiries.map((c) => (
                <button
                  key={c.id}
                  onClick={() => onOpen(c)}
                  data-testid={`week-event-${c.id}`}
                  className="block w-full text-left px-2 py-1.5 rounded-md bg-red-50 border-l-2 border-red-500 hover:bg-red-100 transition-colors"
                  title={itemsLabel(c)}
                >
                  <div className="text-[11px] font-black text-red-900 truncate uppercase">
                    {c.customer}
                  </div>
                  <div className="text-[9px] text-red-700/80 truncate font-arial">
                    {itemsLabel(c)}
                  </div>
                  {c.amount > 0 && (
                    <div className="text-[9px] font-bold text-red-700 tabular-nums mt-0.5">
                      € {fmtMoney(c.amount)}
                    </div>
                  )}
                </button>
              ))}
              {notifications.map((c) => (
                <button
                  key={`n-${c.id}`}
                  onClick={() => onOpen(c)}
                  data-testid={`week-notif-${c.id}`}
                  className="block w-full text-left px-2 py-1.5 rounded-md bg-amber-50 border-l-2 border-amber-500 hover:bg-amber-100 transition-colors"
                  title="ΕΝΗΜΕΡΩΣΗ"
                >
                  <div className="text-[10px] font-black text-amber-900 truncate uppercase flex items-center gap-1">
                    <Bell size={10} weight="bold" /> {c.customer}
                  </div>
                  <div className="text-[9px] text-amber-700/80 truncate">
                    ΕΝΗΜΕΡΩΣΗ
                  </div>
                </button>
              ))}
              {expiries.length === 0 && notifications.length === 0 && (
                <p className="text-[10px] italic text-ink/35 text-center py-4">
                  —
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---------------- Contract Row ---------------- */
function ContractRow({ c, onOpen, showDate, isNotification }) {
  const days = daysUntil(isNotification ? c.notification_date : c.expiry_date);
  let urgency = "text-ink/55 bg-white/60 border-black/10";
  if (days != null) {
    if (days < 0) urgency = "text-red-700 bg-red-50 border-red-200";
    else if (days <= 7) urgency = "text-red-700 bg-red-50 border-red-200";
    else if (days <= 30) urgency = "text-amber-700 bg-amber-50 border-amber-200";
    else urgency = "text-green-700 bg-green-50 border-green-200";
  }
  return (
    <div
      data-testid={`contract-row-${c.id}`}
      className={`rounded-xl border bg-white p-3 transition-all hover:shadow-md hover:border-ink/30 ${
        isNotification ? "border-amber-200 bg-amber-50/40" : "border-black/10"
      }`}
    >
      <div className="flex items-start gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-ink uppercase tracking-wide truncate flex items-center gap-1">
            {isNotification && (
              <Bell size={11} weight="bold" className="text-amber-600" />
            )}
            {c.customer_notified && (
              <CheckCircle
                size={12}
                weight="fill"
                className="text-green-600 flex-shrink-0"
                title="ΕΝΗΜΕΡΩΘΗΚΕ Ο ΠΕΛΑΤΗΣ"
              />
            )}
            <span className="truncate">{c.customer}</span>
          </div>
          <div className="text-[9px] uppercase tracking-[0.16em] text-ink/55 font-bold flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5">
            <span className="font-arial text-ink/80 normal-case tracking-normal">
              {itemsLabel(c)}
            </span>
            {c.code && <span>· {c.code}</span>}
            {c.amount > 0 && (
              <span className="inline-flex items-center gap-0.5 text-green-700">
                <CurrencyEur size={9} weight="bold" />
                {fmtMoney(c.amount)}
              </span>
            )}
            {c.phone && (
              <a
                href={`tel:${c.phone}`}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-0.5 hover:text-ink"
              >
                <Phone size={9} weight="bold" /> {c.phone}
              </a>
            )}
          </div>
          {c.notes && (
            <div className="text-[10px] text-ink/65 italic mt-1 flex items-start gap-1 line-clamp-2">
              <NoteIcon
                size={10}
                weight="bold"
                className="text-ink/45 mt-[1px] flex-shrink-0"
              />
              <span className="truncate">{c.notes}</span>
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          {showDate && (
            <div className="text-[10px] font-bold tabular-nums text-ink/70">
              {fmtGR(c.expiry_date)}
            </div>
          )}
          <span
            className={`text-[9px] uppercase tracking-wider font-black border px-1.5 py-0.5 rounded ${urgency}`}
          >
            {days == null
              ? "—"
              : days < 0
              ? `ΕΛΗΞΕ ${-days}Η`
              : days === 0
              ? "ΣΗΜΕΡΑ"
              : `ΣΕ ${days}Η`}
          </span>
        </div>
      </div>
      <button
        onClick={onOpen}
        data-testid={`contract-open-${c.id}`}
        className="w-full px-3 py-2 rounded-lg bg-ink text-white font-bold text-[11px] uppercase tracking-[0.15em] flex items-center justify-center gap-2 hover:bg-ink/85 active:scale-[0.98] transition-all"
        title="ΑΝΟΙΓΜΑ ΣΥΜΒΟΛΑΙΟΥ"
      >
        <Pencil size={12} weight="bold" />
        ΑΝΟΙΓΜΑ ΣΥΜΒΟΛΑΙΟΥ
        <CaretRightIcon size={12} weight="bold" />
      </button>
    </div>
  );
}

/* ---------------- All Contracts table ---------------- */
const ALL_COLS = [
  { key: "customer", label: "ΠΕΛΑΤΗΣ", grow: true, minWidth: "200px" },
  { key: "items", label: "ΤΥΠΟΣ", grow: true, minWidth: "180px", isItems: true },
  { key: "expiry_date", label: "ΛΗΞΗ", width: "120px", isDate: true, defaultSort: "asc" },
  { key: "amount", label: "ΠΟΣΟ", width: "110px", align: "right", isMoney: true },
  { key: "phone", label: "ΤΗΛΕΦΩΝΟ", width: "150px" },
  {
    key: "notification_date",
    label: "ΕΝΗΜΕΡΩΣΗ",
    width: "120px",
    isDate: true,
  },
  { key: "customer_notified", label: "ΕΝΗΜ.", width: "70px", isBool: true, align: "center" },
  { key: "notes", label: "ΣΧΟΛΙΑ", grow: true, wide: true },
];

function AllContractsTable({ items, onOpen, onRenew, onDelete }) {
  const defaultCol = ALL_COLS.find((c) => c.defaultSort) || ALL_COLS[0];
  const [sortKey, setSortKey] = useState(defaultCol.key);
  const [sortDir, setSortDir] = useState(defaultCol.defaultSort || "asc");
  const [search, setSearch] = useState("");

  const sorted = useMemo(() => {
    const col = ALL_COLS.find((c) => c.key === sortKey) || defaultCol;
    const dir = sortDir === "asc" ? 1 : -1;
    const q = (search || "").trim().toLowerCase();
    const arr = items.filter((c) => {
      if (!q) return true;
      const hay = [
        c.customer,
        c.code,
        c.phone,
        c.notes,
        itemsLabel(c),
        c.activation_date,
        c.expiry_date,
        c.amount != null ? String(c.amount) : "",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
    arr.sort((a, b) => {
      let av = a?.[col.key];
      let bv = b?.[col.key];
      if (col.isItems) {
        av = itemsLabel(a);
        bv = itemsLabel(b);
      }
      if (col.isDate) {
        return ((av || "") > (bv || "") ? 1 : (av || "") < (bv || "") ? -1 : 0) * dir;
      }
      if (col.isBool) return (((av ? 1 : 0) - (bv ? 1 : 0)) * dir);
      if (col.isMoney || typeof av === "number" || typeof bv === "number") {
        const an = parseFloat(av);
        const bn = parseFloat(bv);
        if (!Number.isNaN(an) && !Number.isNaN(bn)) return (an - bn) * dir;
      }
      return (
        String(av || "").localeCompare(String(bv || ""), "el", {
          sensitivity: "base",
        }) * dir
      );
    });
    return arr;
  }, [items, sortKey, sortDir, defaultCol, search]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  return (
    <div className="space-y-3">
      <div
        className="flex items-center gap-2 px-2"
        data-testid="all-contracts-search-bar"
      >
        <MagnifyingGlass size={18} weight="bold" className="text-ink/45" />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="all-contracts-search-input"
          placeholder="ΑΝΑΖΗΤΗΣΗ ΣΕ ΠΕΛΑΤΗ, ΤΥΠΟ, ΤΗΛΕΦΩΝΟ, ΣΧΟΛΙΑ..."
          className="flex-1 max-w-xl px-4 py-2 rounded-full border-2 border-ink/15 bg-white focus:border-ink/40 outline-none text-sm font-arial uppercase tracking-wide placeholder:text-ink/35 placeholder:normal-case placeholder:tracking-normal"
        />
        {search && (
          <button
            onClick={() => setSearch("")}
            data-testid="all-contracts-search-clear"
            className="px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider text-ink/60 hover:text-ink hover:bg-black/5"
          >
            × ΚΑΘΑΡΙΣΜΟΣ
          </button>
        )}
        <span className="text-[10px] uppercase tracking-[0.2em] text-ink/45 font-bold tabular-nums">
          {sorted.length} / {items.length}
        </span>
      </div>

    <div
      className="rounded-xl border border-black/10 bg-white overflow-x-auto shadow-sm"
      data-testid="all-contracts-table"
    >
      <table className="w-full text-sm border-collapse">
        <thead className="bg-ink text-white sticky top-0">
          <tr>
            {ALL_COLS.map((c) => {
              const active = c.key === sortKey;
              const Icon = !active
                ? CaretUpDown
                : sortDir === "asc"
                ? CaretUp
                : CaretDown;
              return (
                <th
                  key={c.key}
                  onClick={() => toggleSort(c.key)}
                  data-testid={`contracts-table-header-${c.key}`}
                  style={
                    c.width
                      ? { width: c.width, minWidth: c.width }
                      : c.minWidth
                      ? { minWidth: c.minWidth }
                      : undefined
                  }
                  className={`px-3 py-2.5 select-none cursor-pointer uppercase tracking-wider text-xs font-black border-r border-white/10 last:border-r-0 hover:bg-ink/85 transition-colors ${
                    c.align === "right"
                      ? "text-right"
                      : c.align === "center"
                      ? "text-center"
                      : "text-left"
                  }`}
                >
                  <div
                    className={`inline-flex items-center gap-1 ${
                      c.align === "right" ? "justify-end" : ""
                    }`}
                  >
                    <span>{c.label}</span>
                    <Icon
                      size={12}
                      weight={active ? "bold" : "regular"}
                      className={active ? "text-amber-300" : "text-white/40"}
                    />
                  </div>
                </th>
              );
            })}
            <th className="px-3 py-2.5 text-right uppercase tracking-wider text-xs font-black w-[200px]">
              ΕΝΕΡΓΕΙΕΣ
            </th>
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 && (
            <tr>
              <td
                colSpan={ALL_COLS.length + 1}
                className="px-3 py-10 text-center italic text-ink/40 text-base"
              >
                ΧΩΡΙΣ ΣΥΜΒΟΛΑΙΑ
              </td>
            </tr>
          )}
          {sorted.map((c, i) => {
            const days = daysUntil(c.expiry_date);
            const overdue = days != null && days < 0;
            const soon = days != null && days >= 0 && days <= 14;
            return (
              <tr
                key={c.id}
                data-testid={`contracts-table-row-${c.id}`}
                onClick={() => onOpen(c)}
                className={`cursor-pointer ${
                  i % 2 === 0 ? "bg-white" : "bg-black/[0.018]"
                } hover:bg-amber-50/60 border-t border-black/5`}
              >
                {ALL_COLS.map((col) => {
                  let display;
                  const raw = c?.[col.key];
                  if (col.isItems) display = itemsLabel(c);
                  else if (col.isDate) display = fmtGR(raw);
                  else if (col.isMoney)
                    display = raw ? `${fmtMoney(raw)} €` : "";
                  else if (col.isBool) display = raw ? "ΝΑΙ" : "";
                  else display = raw;
                  let extra = "";
                  if (col.key === "expiry_date") {
                    if (overdue) extra = " text-red-700 font-bold";
                    else if (soon) extra = " text-amber-700 font-bold";
                  }
                  return (
                    <td
                      key={col.key}
                      style={
                        col.width
                          ? { width: col.width, minWidth: col.width }
                          : col.minWidth
                          ? { minWidth: col.minWidth }
                          : undefined
                      }
                      className={`px-3 py-2 border-r border-black/5 last:border-r-0 ${
                        col.align === "right"
                          ? "text-right tabular-nums font-bold"
                          : col.align === "center"
                          ? "text-center"
                          : "text-left"
                      } ${col.wide ? "max-w-[260px]" : ""}${extra}`}
                    >
                      <div
                        className={`truncate ${
                          col.wide
                            ? "whitespace-pre-line break-words line-clamp-2"
                            : ""
                        }`}
                        title={String(display || "")}
                      >
                        {display || <span className="text-ink/25">—</span>}
                      </div>
                    </td>
                  );
                })}
                <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="inline-flex items-center gap-1.5">
                    <button
                      onClick={() => onOpen(c)}
                      data-testid={`contract-open-row-${c.id}`}
                      title="ΑΝΟΙΓΜΑ"
                      className="px-2.5 py-1.5 rounded text-xs font-bold uppercase tracking-wider border-2 border-ink/30 bg-white text-ink hover:bg-stone-50 active:scale-95 inline-flex items-center gap-1"
                    >
                      <Pencil size={13} weight="bold" />
                      ΑΝΟΙΓΜΑ
                    </button>
                    <button
                      onClick={() => onRenew(c)}
                      data-testid={`contract-renew-row-${c.id}`}
                      title="ΔΗΜΙΟΥΡΓΙΑ POST-IT ΑΝΑΝΕΩΣΗΣ"
                      className="px-2.5 py-1.5 rounded text-xs font-bold uppercase tracking-wider border-2 border-emerald-700 bg-white text-emerald-700 hover:bg-emerald-50 active:scale-95 inline-flex items-center gap-1"
                    >
                      <ArrowsClockwise size={13} weight="bold" />
                      ΑΝΑΝΕΩΣΗ
                    </button>
                    <button
                      onClick={() => onDelete(c)}
                      data-testid={`contract-delete-row-${c.id}`}
                      title="ΔΙΑΓΡΑΦΗ"
                      className="px-2.5 py-1.5 rounded text-xs font-bold uppercase tracking-wider border-2 border-red-300 bg-white text-red-700 hover:bg-red-50 active:scale-95 inline-flex items-center gap-1"
                    >
                      <Trash size={13} weight="bold" />
                      ΔΙΑΓΡ.
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    </div>
  );
}
